import type { Device } from '@/types';

/**
 * Primary identifier shown for a device in pickers and search results.
 *
 * Backend device search matches on `devices.machine_id`, so machine_id is the
 * correct primary label here. It is optional on the API type (unenrolled /
 * legacy rows), so fall back to the DB device UUID — the value submitted on
 * selection — to keep the option non-empty. Keep this to a single fallback;
 * if a friendlier asset number is wanted, show it as secondary metadata
 * rather than as the primary identifier used for selection.
 */
export function getDeviceLabel(device: Pick<Device, 'machine_id' | 'id'>): string {
  return device.machine_id || device.id;
}

/**
 * Full device display label used in dialogs and confirmations:
 * `number → serial_number → machine_id → id`, empty string when none resolve.
 * Accepts a possibly-null device so nullable dialog state can be passed
 * directly; call sites apply `.toUpperCase()` as needed.
 */
export function getDeviceDisplayLabel(
  device: Partial<Pick<Device, 'number' | 'serial_number' | 'machine_id' | 'id'>> | null | undefined,
): string {
  return device?.number || device?.serial_number || device?.machine_id || device?.id || '';
}

/**
 * Device display fields embedded on each group member by the group API so
 * member rows can render without a separate device fetch. Optional because
 * legacy rows may leave some blank.
 */
export interface MemberDeviceFields {
  machine_id?: string;
  serial_number?: string;
  number?: string;
  description?: string;
  type?: string;
  model?: string;
}

export interface ResolvedMemberDevice {
  machineId: string;
  serialNumber: string;
  number: string;
  description: string;
  type: string;
  model: string;
}

/**
 * Normalize a group member's embedded device display fields into a
 * camelCased object with empty-string defaults for rendering.
 */
export function resolveMemberDevice(member: MemberDeviceFields): ResolvedMemberDevice {
  return {
    machineId: member.machine_id || '',
    serialNumber: member.serial_number || '',
    number: member.number || '',
    description: member.description || '',
    type: member.type || '',
    model: member.model || '',
  };
}

/**
 * Primary label for a group member row. Mirrors the device-list label
 * (`number → serial_number`) but adds machine_id before the UUID fallback so
 * a device added via the machine_id search still reads sensibly, and only
 * shows the raw `device_id` when nothing else is available.
 */
export function getMemberLabel(device: ResolvedMemberDevice, deviceId: string): string {
  return (device.number || device.serialNumber || device.machineId || deviceId).toUpperCase();
}
